import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  UpsertEntityInput,
  useUpsertEntity,
} from 'src/data-capture/merge-review/useUpsertEntities';
import { useConnectAsset } from 'src/financial/financial-data/hooks/useConnectAsset';
import { FinancialCategory } from 'src/financial/financial-data/hooks/useFinancialItems';
import useFinancialEntityLayout from 'src/UI/dynamicUI/hooks/useFinancialEntityLayout';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { ApiError, ApiErrorCode } from 'src/error/ApiError';
import { Logger } from 'src/utils/Logger';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { useFamilyMembers } from 'src/non-financial-data/v2/family/useFamilyMembers';
import {
  normaliseOwnersForPayload,
  productSupportsOwners,
  type OwnerRowValue,
} from 'src/financial/financial-data/financial-item/forms/common/OwnerSharesSection';
import { AssetUpdateChoiceModal, ProviderReferenceModal } from './AssetCreationModals';
import { FamilyOwnerModal, type OwnerSelection } from './FamilyOwnerModal';

type PendingCreate = { category: FinancialCategory; product: string };

type UseCreateFinancialItemArgs = {
  clientId: string;
  onNavigate?: () => void;
  // The entity layout is needed to build a connected asset on submit. Callers
  // that let the user browse products before committing (the picker) pass the
  // current selection so the layout is prefetched rather than fetched only once
  // creation begins.
  prefetchEntityType?: string;
};

export function useCreateFinancialItem({
  clientId,
  onNavigate,
  prefetchEntityType,
}: UseCreateFinancialItemArgs) {
  const navigate = useNavigate();
  const [pending, setPending] = useState<PendingCreate | null>(null);
  const [ownerModalOpened, setOwnerModalOpened] = useState(false);
  const [selectedOwners, setSelectedOwners] = useState<OwnerSelection[]>([]);
  const [choiceModalOpened, setChoiceModalOpened] = useState(false);
  const [providerModalOpened, setProviderModalOpened] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<'connection' | 'manually'>('connection');
  const [isUpserting, setIsUpserting] = useState(false);

  const { flagEnabledFamilyScreenRework, flagEnabledFinancialItemOwners } = useFlags();
  const { members, hasOtherMembers } = useFamilyMembers({
    enabled: flagEnabledFamilyScreenRework && flagEnabledFinancialItemOwners,
  });
  const showOwnerStep =
    flagEnabledFinancialItemOwners && flagEnabledFamilyScreenRework && hasOtherMembers;

  const { upsertEntity } = useUpsertEntity();
  const { layout, isLoading: isLoadingLayout } = useFinancialEntityLayout({
    entityType: pending?.product ?? prefetchEntityType,
  });
  const { connectAsset } = useConnectAsset({ clientId });

  const navigateAndClose = (path: string) => {
    navigate(path);
    onNavigate?.();
  };

  const buildCreateUrl = (
    product: string,
    extra?: Record<string, string>,
    owners: OwnerSelection[] = selectedOwners,
  ) => {
    const params = new URLSearchParams(extra);
    if (owners.length > 0) {
      params.set('owners', JSON.stringify(owners));
      const internalCount = owners.filter(owner => 'client_id' in owner).length;
      params.set('joint', internalCount >= 2 ? 'true' : 'false');
    }
    const query = params.toString();
    return `/client/${clientId}/financial-items/create/${encodeURIComponent(product)}${query ? `?${query}` : ''}`;
  };

  const proceedAfterOwners = (
    category: FinancialCategory,
    product: string,
    owners: OwnerSelection[],
  ) => {
    if (category === 'ASSET') {
      setChoiceModalOpened(true);
    } else {
      navigateAndClose(buildCreateUrl(product, undefined, owners));
    }
  };

  const beginCreate = (category: FinancialCategory, product: string) => {
    setPending({ category, product });
    setSelectedOwners([]);
    if (showOwnerStep && productSupportsOwners(product)) {
      setOwnerModalOpened(true);
    } else {
      proceedAfterOwners(category, product, []);
    }
  };

  const handleOwnersContinue = (owners: OwnerSelection[]) => {
    setOwnerModalOpened(false);
    setSelectedOwners(owners);
    if (pending) {
      proceedAfterOwners(pending.category, pending.product, owners);
    }
  };

  const handleContinue = () => {
    setChoiceModalOpened(false);
    if (!pending) return;
    if (selectedMethod === 'manually') {
      navigateAndClose(buildCreateUrl(pending.product));
    } else {
      setProviderModalOpened(true);
    }
  };

  const handleProviderSubmit = async (identifiers: {
    providerReference?: string;
    policyNumber?: string;
  }) => {
    setProviderModalOpened(false);
    const product = pending?.product;
    const { providerReference, policyNumber } = identifiers;

    if (pending?.category === 'ASSET' && product && clientId && layout) {
      try {
        setIsUpserting(true);

        const metadata = layout.entitiesMetadata?.[product];
        if (!metadata) {
          throw new Error(`No metadata found for entity type: ${product}`);
        }

        const includeOwners = flagEnabledFinancialItemOwners && selectedOwners.length > 0;
        const entityValues = {
          ...(providerReference && { provider_reference_number: providerReference }),
          ...(policyNumber && { policy_number: policyNumber }),
          ...(includeOwners
            ? { owners: normaliseOwnersForPayload(selectedOwners as OwnerRowValue[]) }
            : {}),
        };

        const upsertInput: UpsertEntityInput = {
          entityFieldsMetadata: metadata,
          entityType: product,
          values: entityValues,
        };
        const upsertedEntityId = await upsertEntity(clientId, upsertInput, true);

        if (upsertedEntityId) {
          try {
            await connectAsset(upsertedEntityId);

            notifications.show({
              title: 'Success',
              message: 'Asset connection created and populated successfully',
              color: 'green',
            });
          } catch (connectError) {
            if (!ApiError.isApiError(ApiErrorCode.NO_UNPROCESSED_VALUATION_UPDATE, connectError)) {
              Logger.warn('Failed to connect asset to feed after creation', {
                connectError,
                assetId: upsertedEntityId,
              });
            }

            notifications.show({
              title: 'Partially successful',
              message:
                'Asset created successfully, but failed to connect to feed data. You can manually connect it later.',
              color: 'yellow',
            });
          }

          navigateAndClose(
            `/client/${clientId}/financial-items/${encodeURIComponent(product)}/${encodeURIComponent(upsertedEntityId)}/`,
          );
        } else {
          throw new Error('Failed to create asset connection');
        }
      } catch (error) {
        notifyAndLogError(
          'Error creating asset connection',
          'Something went wrong trying to create the asset connection. Please try again and contact support if the issue persists.',
          error,
          { entityType: product, providerReference, policyNumber },
        );
      } finally {
        setIsUpserting(false);
      }
    } else {
      const extra: Record<string, string> = {};
      if (providerReference) extra.providerReference = providerReference;
      if (policyNumber) extra.policyNumber = policyNumber;
      navigateAndClose(buildCreateUrl(product ?? '', extra));
    }
  };

  const modals = (
    <>
      <FamilyOwnerModal
        opened={ownerModalOpened}
        onClose={() => setOwnerModalOpened(false)}
        members={members}
        currentClientId={clientId}
        onContinue={handleOwnersContinue}
      />
      <AssetUpdateChoiceModal
        opened={choiceModalOpened}
        onClose={() => setChoiceModalOpened(false)}
        selectedMethod={selectedMethod}
        onMethodChange={setSelectedMethod}
        onContinue={handleContinue}
      />
      <ProviderReferenceModal
        opened={providerModalOpened}
        onClose={() => setProviderModalOpened(false)}
        onSubmit={handleProviderSubmit}
        isLoading={isUpserting || isLoadingLayout}
        clientId={clientId}
      />
    </>
  );

  return { beginCreate, modals };
}
